import { useCallback, useEffect, useRef, useState } from "react";
import { resolveSchemeColors } from "@/constants/landing";

/** The three stages the recipient gift-experience preview walks through, in order. */
export type GiftPreviewStage = "email" | "unboxing" | "landing";

interface UseGiftPreviewFlowOptions {
  /** Manual-send campaigns never email, so `reset()` opens on "unboxing" instead of "email". */
  manual: boolean;
  confettiEnabled: boolean;
  confettiScheme: number | null;
}

interface UseGiftPreviewFlowResult {
  stage: GiftPreviewStage;
  /** Call synchronously alongside opening the overlay (same click handler) so the first
   *  commit already has the reset state — see RecipientExperienceCard.handlePreview. */
  reset: () => void;
  advanceToUnboxing: () => void;
  ubPlayTrigger: number;
  handleUnboxingVideoEnd: () => void;
  giftMsgOpen: boolean;
  openGiftMsg: () => void;
  closeGiftMsg: () => void;
  handleUnwrapMessage: () => void;
  lpVideoOpen: boolean;
  openLpVideo: () => void;
  closeLpVideo: () => void;
  confettiTrigger: number;
  confettiColors: string[] | undefined;
}

/**
 * Drives the Step 4 "Preview Gift Experience" overlay through the same three stages
 * DesignStep's fullscreen preview does, off LOCAL state rather than the shared
 * `designActiveTab` context value — see RecipientExperienceCard for why.
 */
export function useGiftPreviewFlow({
  manual,
  confettiEnabled,
  confettiScheme,
}: UseGiftPreviewFlowOptions): UseGiftPreviewFlowResult {
  const [stage, setStage] = useState<GiftPreviewStage>(manual ? "unboxing" : "email");
  const [ubPlayTrigger, setUbPlayTrigger] = useState(0);
  const [giftMsgOpen, setGiftMsgOpen] = useState(false);
  const [lpVideoOpen, setLpVideoOpen] = useState(false);
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const [confettiColors, setConfettiColors] = useState<string[] | undefined>(undefined);

  const reset = useCallback(() => {
    setStage(manual ? "unboxing" : "email");
    setGiftMsgOpen(false);
    setLpVideoOpen(false);
  }, [manual]);

  const advanceToUnboxing = useCallback(() => setStage("unboxing"), []);
  const handleUnboxingVideoEnd = useCallback(() => setStage("landing"), []);

  const openGiftMsg = useCallback(() => setGiftMsgOpen(true), []);
  const closeGiftMsg = useCallback(() => setGiftMsgOpen(false), []);
  const handleUnwrapMessage = useCallback(() => {
    setGiftMsgOpen(false);
    setUbPlayTrigger((t) => t + 1);
  }, []);

  const openLpVideo = useCallback(() => setLpVideoOpen(true), []);
  const closeLpVideo = useCallback(() => setLpVideoOpen(false), []);

  // Ref-guarded so the burst fires once per arrival at "landing", not on every re-render
  // while already there.
  const prevStageRef = useRef<GiftPreviewStage>(stage);
  useEffect(() => {
    const entered = stage === "landing" && prevStageRef.current !== "landing";
    prevStageRef.current = stage;
    if (entered && confettiEnabled) {
      setConfettiColors(resolveSchemeColors(confettiScheme));
      setConfettiTrigger((t) => t + 1);
    }
  }, [stage, confettiEnabled, confettiScheme]);

  return {
    stage,
    reset,
    advanceToUnboxing,
    ubPlayTrigger,
    handleUnboxingVideoEnd,
    giftMsgOpen,
    openGiftMsg,
    closeGiftMsg,
    handleUnwrapMessage,
    lpVideoOpen,
    openLpVideo,
    closeLpVideo,
    confettiTrigger,
    confettiColors,
  };
}
